import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

export default function LoginPage() {
  const { settings } = useSettings();
  const brandLogo = settings?.company_logo || '/logo.png';
  const companyName = settings?.company_name || 'DECEDAS GLOBAL RESOURCES LIMITED';
  const nameParts = companyName.split(' ');
  const heroTitle = nameParts[0] || 'DECEDAS';
  const heroSubtitle = nameParts.slice(1).join(' ') || 'Global Resources Limited';
  const companyTagline = settings?.company_tagline || 'Excellence in Every Transaction';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lux-black flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1706808849802-8f876ade0d1f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-lux-black/90 via-lux-black/70 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <div>
            <img
              src={brandLogo}
              alt="Decedas Global Resources Logo"
              className="h-16 w-auto max-w-[220px] object-contain mb-4"
              data-testid="login-hero-company-logo"
            />
            <h1 className="font-heading text-4xl text-lux-gold font-bold tracking-tight">
              {heroTitle}
            </h1>
            <p className="text-lux-muted mt-2 tracking-widest uppercase text-sm">
              {heroSubtitle}
            </p>
          </div>
          <div className="max-w-md">
            <h2 className="font-heading text-4xl text-lux-text mb-4">
              {companyTagline}
            </h2>
            <p className="text-lux-muted text-lg leading-relaxed">
              Your premier partner in luxury real estate. Managing properties, tracking sales, and building wealth across Nigeria.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-12">
            <img
              src={brandLogo}
              alt="Decedas Global Resources Logo"
              className="h-14 w-auto max-w-[200px] object-contain mx-auto mb-4"
              data-testid="login-mobile-company-logo"
            />
            <h1 className="font-heading text-3xl text-lux-gold font-bold tracking-tight">
              {heroTitle}
            </h1>
            <p className="text-lux-muted mt-1 tracking-widest uppercase text-xs">
              {heroSubtitle}
            </p>
          </div>

          <div className="lux-card p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-lux-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-lux-border">
                <img
                  src={brandLogo}
                  alt="Company Mark"
                  className="h-12 w-auto max-w-[64px] object-contain"
                  data-testid="login-card-company-logo"
                />
              </div>
              <h2 className="font-heading text-3xl text-lux-text mb-2">Welcome Back</h2>
              <p className="text-lux-muted">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-lux-text">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-lux-muted" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="lux-input pl-10"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-lux-text">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-lux-muted" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="lux-input pl-10 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-lux-muted hover:text-lux-text"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full lux-btn-primary h-12 text-lg"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-lux-black/30 border-t-lux-black rounded-full animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
